import { listingsApi } from "@/lib/api/listings/api";
import { adminService } from "@/lib/api/services/admin";
import {
  AdminListing,
  AdminListingFilters,
  StatusChangeRequest,
} from "@/lib/types/adminTypes";
import { ListingInput } from "@/lib/types/listingTypes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export function useAdminListings(filters: AdminListingFilters = {}) {
  return useQuery({
    queryKey: ["adminListings", filters],
    queryFn: async () => {
      try {
        const response = await adminService.getAllListings(filters);

        // Normalize response format according to the API spec
        if (response && response.data) {
          return {
            listings: response.data.data?.listings || [],
            pagination: response.data.data?.pagination || {
              totalItems: 0,
              totalPages: 1,
              currentPage: 1,
              itemsPerPage: 10,
            },
          };
        } else {
          console.error("Unexpected API response format:", response);
          return {
            listings: [],
            pagination: {
              totalItems: 0,
              totalPages: 1,
              currentPage: 1,
              itemsPerPage: 10,
            },
          };
        }
      } catch (error) {
        console.error("Error fetching admin listings:", error);
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    retry: 1, // Only retry once for admin endpoints
    meta: {
      errorMessage: "Failed to fetch admin listings",
    },
  });
}

export function useAdminListing(id: number | string | undefined) {
  return useQuery({
    queryKey: ["adminListing", id],
    queryFn: async () => {
      if (!id) throw new Error("Listing ID is required");
      try {
        const response = await adminService.getListingDetails(id);
        if (response && response.data && response.data.data) {
          return response.data.data;
        }
        throw new Error(
          "Invalid API response structure for admin listing details"
        );
      } catch (error) {
        console.error(`Error fetching admin listing ${id}:`, error);
        throw error;
      }
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    retry: 1, // Only retry once for admin endpoints
  });
}

export function useAdminDashboardStats() {
  return useQuery({
    queryKey: ["adminDashboardStats"],
    queryFn: async () => {
      try {
        const response = await adminService.getDashboardStats();

        // Ensure we're returning the actual stats object from response data
        return response.data.data;
      } catch (error) {
        console.error("Error fetching admin dashboard stats:", error);
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    retry: 1, // Only retry once for admin endpoints
    meta: {
      errorMessage: "Failed to load dashboard statistics",
    },
  });
}

export function useAdminListingMutations() {
  const queryClient = useQueryClient();

  // POST /api/admin/listings (using the new service method)
  const createListing = useMutation({
    mutationFn: (data: ListingInput & { user_id: number }) =>
      adminService.createAdminListing(data),
    onSuccess: (data: any, variables) => {
      // More specific success message
      const title = variables.title || "New Listing";
      toast.success(`Listing "${title}" created successfully.`);
      queryClient.invalidateQueries({ queryKey: ["adminListings"] });
      queryClient.invalidateQueries({ queryKey: ["adminDashboardStats"] });
      // We return the data so handleCreate can get the ID
      return data;
    },
    onError: (error) => {
      console.error("Error creating listing:", error);
      const errorMsg =
        (error as any)?.response?.data?.message ||
        "Failed to create listing. Please check inputs and try again."; // User-friendlier fallback
      toast.error(errorMsg);
    },
  });

  // PUT /api/admin/listings/{id}
  const updateListing = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number | string;
      data: Partial<AdminListing>;
    }) => adminService.updateListing(id, data),
    onSuccess: (data, variables) => {
      // We don't show success here; handleSave in EditListing shows a summary toast
      // toast.success("Listing updated successfully");
      queryClient.invalidateQueries({ queryKey: ["adminListings"] });
      queryClient.invalidateQueries({
        queryKey: ["adminListing", variables.id],
      });
      queryClient.invalidateQueries({ queryKey: ["adminDashboardStats"] });
      // Return data in case it's needed
      return data;
    },
    onError: (error, variables) => {
      console.error(`Error updating listing ${variables.id}:`, error);
      const errorMsg =
        (error as any)?.response?.data?.message ||
        "Failed to update listing. Please try again.";
      toast.error(errorMsg);
    },
  });

  // DELETE /api/admin/listings/{id}
  const deleteListing = useMutation({
    mutationFn: (id: number | string) => adminService.deleteListing(id),
    onSuccess: (data, variables) => {
      toast.success(`Listing (ID: ${variables}) deleted successfully.`);
      queryClient.invalidateQueries({ queryKey: ["adminListings"] });
      queryClient.invalidateQueries({ queryKey: ["adminDashboardStats"] });
      // Maybe invalidate the specific listing query although it's gone
      queryClient.removeQueries({ queryKey: ["adminListing", variables] });
    },
    onError: (error, variables) => {
      console.error(`Error deleting listing ${variables}:`, error);
      toast.error(
        "Failed to delete listing. It might be referenced elsewhere."
      );
    },
  });

  // PATCH /api/admin/listings/{id}/status
  const changeStatus = useMutation({
    mutationFn: ({
      id,
      statusData,
    }: {
      id: number | string;
      statusData: StatusChangeRequest;
    }) => adminService.changeListingStatus(id, statusData),
    onSuccess: (data, variables) => {
      const newStatus = variables.statusData.status;
      toast.success(
        `Listing (ID: ${variables.id}) status changed to ${newStatus}.`
      );
      queryClient.invalidateQueries({ queryKey: ["adminListings"] });
      // Invalidate the specific listing to reflect the status change
      queryClient.invalidateQueries({
        queryKey: ["adminListing", variables.id],
      });
      queryClient.invalidateQueries({ queryKey: ["adminDashboardStats"] });
    },
    onError: (error, variables) => {
      console.error(
        `Error changing status for listing ${variables.id}:`,
        error
      );
      toast.error("Failed to change listing status.");
    },
  });

  // Bulk operations
  const bulkDeleteListings = useMutation({
    mutationFn: (ids: (number | string)[]) =>
      adminService.bulkDeleteListings(ids),
    onSuccess: (data, variables) => {
      toast.success(`${variables.length} listings deleted successfully.`);
      queryClient.invalidateQueries({ queryKey: ["adminListings"] });
      queryClient.invalidateQueries({ queryKey: ["adminDashboardStats"] });
      // Remove individual listing queries if needed
      variables.forEach((id) =>
        queryClient.removeQueries({ queryKey: ["adminListing", id] })
      );
    },
    onError: (error, variables) => {
      console.error(`Error deleting ${variables.length} listings:`, error);
      toast.error("Failed to delete selected listings.");
    },
  });

  const bulkUpdateStatus = useMutation({
    mutationFn: ({
      ids,
      statusData,
    }: {
      ids: (number | string)[];
      statusData: StatusChangeRequest;
    }) => adminService.bulkUpdateStatus(ids, statusData),
    onSuccess: (_, variables) => {
      toast.success(
        `Status updated to ${variables.statusData.status} for ${variables.ids.length} listings.`
      );
      queryClient.invalidateQueries({ queryKey: ["adminListings"] });
      queryClient.invalidateQueries({ queryKey: ["adminDashboardStats"] });
      // Invalidate individual listings
      variables.ids.forEach((id) =>
        queryClient.invalidateQueries({ queryKey: ["adminListing", id] })
      );
    },
    onError: (error, variables) => {
      console.error(
        `Error updating status for ${variables.ids.length} listings:`,
        error
      );
      toast.error("Failed to update status for selected listings.");
    },
  });

  // POST /api/listings/{id}/images
  const uploadImage = useMutation({
    mutationFn: ({
      listingId,
      imageFile,
    }: {
      listingId: number | string;
      imageFile: File;
    }) => listingsApi.uploadListingImage(listingId, imageFile),
    onSuccess: (data, variables) => {
      // Don't show individual toasts here; handleCreate/handleSave show summary
      // toast.success(`Image uploaded successfully for listing ${variables.listingId}`);
      queryClient.invalidateQueries({
        queryKey: ["adminListing", variables.listingId],
      });
    },
    onError: (error, variables) => {
      console.error(
        `Error uploading image for listing ${variables.listingId}:`,
        error
      );
      // Don't show individual toasts here; handleCreate/handleSave show summary
      // toast.error(`Failed to upload image: ${variables.imageFile.name}`);
    },
  });

  // DELETE /api/listings/{id}/images/{imageId}
  const deleteImage = useMutation({
    mutationFn: ({
      listingId,
      imageId,
    }: {
      listingId: number | string;
      imageId: number | string;
    }) => listingsApi.deleteListingImage(listingId, imageId),
    onSuccess: (_, variables) => {
      // Don't show individual toasts here; handleSave shows summary
      // toast.success(`Image (ID: ${variables.imageId}) deleted successfully.`);
      queryClient.invalidateQueries({
        queryKey: ["adminListing", variables.listingId],
      });
    },
    onError: (error, variables) => {
      console.error(
        `Error deleting image ${variables.imageId} for listing ${variables.listingId}:`,
        error
      );
      // Don't show individual toasts here; handleSave shows summary
      // toast.error(`Failed to delete image (ID: ${variables.imageId}).`);
    },
  });

  return {
    createListing,
    updateListing,
    deleteListing,
    changeStatus,
    bulkDeleteListings,
    bulkUpdateStatus,
    uploadImage,
    deleteImage,
  };
}
